<script setup>
import Icon from '@/Components/UI/Icon.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'

/**
 * Segmented toggle for table/cards/compact layouts on data-bearing pages.
 * Usage:
 *   const mode = useLayoutMode('rentals', MODES, 'table')
 *   <LayoutToggle :modes="MODES" v-model="mode" />
 */
defineProps({
  modes: {
    type: Array,
    required: true, // [{ v, label, icon }]
  },
  modelValue: { type: String, required: true },
})
defineEmits(['update:modelValue'])

const { isMobile } = useBreakpoint()
</script>

<template>
  <div
    v-if="!isMobile"
    class="layout-toggle"
  >
    <button
      v-for="m in modes"
      :key="m.v"
      :title="m.label"
      :class="{ active: modelValue === m.v }"
      @click="$emit('update:modelValue', m.v)"
    >
      <Icon
        :name="m.icon"
        :size="14"
      />
      <span class="hidden sm:inline">{{ m.label }}</span>
    </button>
  </div>
</template>

<style scoped>
.layout-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 36px;
  background: var(--surfaceAlt);
  padding: 2px;
  border-radius: 999px;
  border: 1px solid var(--line);
}
.layout-toggle button {
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  background: transparent;
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 400;
  transition: background 120ms, color 120ms;
}
.layout-toggle button.active {
  background: var(--surface);
  color: var(--ink);
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}
</style>
